import React, { useContext, useState } from "react";

import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormLabel,
  Grid,
} from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { updateSubscriptionComponentPrice } from "api/endpoints/components";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { GrpcError } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { getComponentPriceLabel } from "helpers/get-component-price-label";
import { toUUID } from "helpers/s12-helpers";
import {
  ChangeComponentPricePointConfirmProps,
  UrlParamTypes,
} from "types/org-display";

const ChangeComponentPricePointConfirm = ({
  currPrice,
  newPrice,
  subId,
  chargifySite,
  onCancel,
  onComplete,
}: ChangeComponentPricePointConfirmProps) => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [, setOpenConfirmDialog] = useState(true);
  const [showModal] = useState(true);
  const [priceConfirmed, setPriceConfirmed] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<GrpcError>();
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const isButtonDisabled = isSubscriptionActionLoading || !priceConfirmed;

  const submitPricePointChange = async () => {
    setIsSubscriptionActionLoading(true);
    try {
      await updateSubscriptionComponentPrice(
        {
          subId: toUUID(subId),
          componentPriceId: newPrice.id,
          organizationId: toUUID(roleId),
        },
        callOptionsHeaders(regionCode, roleId)
      );
      setIsProcessing(false);
      onComplete();
    } catch (e) {
      setError(e as GrpcError);
    } finally {
      setIsProcessing(false);
      setIsSubscriptionActionLoading(false);
    }
  };

  const renderErrorMessage = () => {
    if (!error) {
      return;
    }
    return (
      <Alert severity="error" variant="filled" data-test-id="error-message">
        {error?.message ==
        "failed to retrieve a chargify component price map by component price ID"
          ? `The price point has not been set in Chargify (${chargifySite})`
          : error?.message}
      </Alert>
    );
  };

  return (
    <>
      {isProcessing ? (
        <Grid
          container
          justifyContent="center"
          className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
        >
          <CircularProgress
            color="primary"
            variant="indeterminate"
            thickness={4}
            size={30}
            data-testid="loading-icon"
          />
        </Grid>
      ) : (
        <ConfirmDialog
          title="Please review the details"
          cancelBtnLabel="Cancel"
          confirmBtnLabel="Confirm"
          open={showModal}
          setOpen={setOpenConfirmDialog}
          onConfirm={() => {
            setIsProcessing(true);
            submitPricePointChange();
          }}
          onCancel={() => onCancel({}, "normal")}
          disabled={isButtonDisabled}
        >
          <Box
            sx={{ width: "100%", paddingTop: "0.5rem" }}
            className={clsx(classes.spaceTopSmall)}
          >
            <FormLabel
              component="legend"
              className={clsx(classes.spaceTopSmall)}
              data-testid="change-component-price-current-price"
            >
              <b>Current price:</b> {getComponentPriceLabel(currPrice, "seat")}
            </FormLabel>
            <FormLabel
              component="legend"
              className={clsx(classes.spaceTopSmall)}
              data-testid="change-component-price-new-price"
            >
              <b>New price:</b> {getComponentPriceLabel(newPrice, "seat")}
            </FormLabel>
          </Box>
          <FormControlLabel
            className={clsx(classes.spaceTopSmall)}
            label="New price is correct"
            data-testid="price-confirm"
            control={
              <Checkbox
                checked={priceConfirmed}
                disabled={isProcessing}
                onChange={() => setPriceConfirmed(!priceConfirmed)}
              />
            }
            style={{ width: "100%" }}
          />
          <Box
            sx={{ width: "100%", paddingTop: "0.5rem" }}
            className={clsx(classes.spaceTopSmall)}
          >
            {renderErrorMessage()}
          </Box>
        </ConfirmDialog>
      )}
    </>
  );
};

export default ChangeComponentPricePointConfirm;
